import React from 'react';

import { useFlags } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/core/check-mark';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { G300, R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { updateCombinedConfigCacheEntry } from '@atlassian/search-client/config';
import { update3pConfigCacheEntry } from '@atlassian/search-client/fetch-search-config';
import { searchPageQueryCache } from '@atlassian/search-client/search-page';
import { sendSlackAuthSuccessRequest } from '@atlassian/search-client/slack';
import { sendTeamsAuthSuccessRequest } from '@atlassian/search-client/teams';

import { type NounKeys } from '../../common/constants/nouns';
import {
	is3pProductKey,
	type ProductKeys,
	type ProductKeys3P,
} from '../../common/constants/products';
import {
	type ConnectorSource,
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../common/constants/schemas/3p-config';
import { useAppContext } from '../../common/ui/app-context';
import type { OAuthSource } from '../../common/utils/events/oauth-events';
import { serveSearchCombinedConfigExperiment } from '../../common/utils/experiments';
import { getThirdPartyTypes } from '../config/utils/third-party-products';
import {
	useFireOAuthCompleted,
	useFireOAuthFailed,
	useFireOAuthStarted,
} from '../events/use-fire-oauth-events';
import {
	useCloudId,
	useSetThirdPartyConfigs,
	useThirdPartyConfigs,
	useUser,
} from '../store/bootstrap';
import { useQuickFindActions } from '../store/quick-find';
import { useSearchActions } from '../store/search';

interface OAuthHandlersProps {
	product: ProductKeys | NounKeys | undefined;
	connectorSource?: ConnectorSource;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	onFailAction?: () => void;
	isOnSearchDialog?: boolean;
	isBulkConnect?: boolean;
	query?: string;
	datasourceId?: string;
	extraAttributes?: Record<string, unknown>;
}

const refreshOAuthCache = (
	product: ProductKeys,
	userId: string,
	cloudId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	datasourceId?: string,
): ThirdPartyConfigsBootstrap => {
	const newThirdPartyConfigs = { ...thirdPartyConfigs };

	// Update in-app bootstrap state synchronously for immediate Connect CTA removal.
	const updatedConfig = newThirdPartyConfigs[product as ProductKeys3P];
	if (updatedConfig) {
		if (datasourceId) {
			newThirdPartyConfigs[product as ProductKeys3P] = {
				...updatedConfig,
				datasources: updatedConfig.datasources?.map((ds) =>
					ds.datasourceId === datasourceId ? { ...ds, userNeedsOAuth: false } : ds,
				),
			};
		} else {
			// Create a new object instead of mutating in place, so that React
			// detects the change when comparing old vs new references.
			newThirdPartyConfigs[product as ProductKeys3P] = {
				...updatedConfig,
				userNeedsOAuth: false,
			};
		}
	}

	// Update the persistent cache so subsequent page loads also reflect the connected state.
	// Patch via promiseCache.update() so both in-memory cache and localStorage stay
	// consistent without manually parsing raw JSON. Mirrors refreshCombinedOAuthCache.
	const thirdPartyTypes = getThirdPartyTypes();
	update3pConfigCacheEntry(userId, cloudId, (resources) =>
		resources.map((resource) => {
			const matchesDatasource = datasourceId ? resource.datasourceId === datasourceId : false;
			const matchesProduct = !datasourceId && thirdPartyTypes[resource.type] === product;
			if (matchesDatasource || matchesProduct) {
				return { ...resource, isUserOAuthed: true };
			}
			return resource;
		}),
	);

	return newThirdPartyConfigs;
};

/**
 * Combined-config variant of refreshOAuthCache.
 *
 * Updates the in-memory bootstrap configs synchronously (for immediate UI feedback)
 * and asynchronously updates the combined-config promiseCache so that both the
 * in-memory cache and localStorage stay consistent for subsequent page loads.
 *
 * Unlike refreshOAuthCache, this always produces updated configs with
 * userNeedsOAuth=false regardless of localStorage state, because the
 * authoritative cache is the promiseCache (updated separately).
 */
const refreshCombinedOAuthCache = (
	product: ProductKeys,
	userId: string,
	cloudId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	datasourceId?: string,
): ThirdPartyConfigsBootstrap => {
	const newThirdPartyConfigs = { ...thirdPartyConfigs };
	const productKey = product as ProductKeys3P;
	const existingConfig = newThirdPartyConfigs[productKey];

	if (existingConfig) {
		if (datasourceId) {
			newThirdPartyConfigs[productKey] = {
				...existingConfig,
				datasources: existingConfig.datasources?.map((ds) =>
					ds.datasourceId === datasourceId ? { ...ds, userNeedsOAuth: false } : ds,
				),
			};
		} else {
			newThirdPartyConfigs[productKey] = {
				...existingConfig,
				userNeedsOAuth: false,
			};
		}
	}

	const thirdPartyTypes = getThirdPartyTypes();

	updateCombinedConfigCacheEntry(userId, cloudId, (config) => ({
		...config,
		thirdPartyProducts: config.thirdPartyProducts?.map((entry) => {
			if (datasourceId) {
				if (entry.datasourceId === datasourceId) {
					return { ...entry, isUserOAuthed: true };
				}
			} else if (thirdPartyTypes[entry.name] === product) {
				return { ...entry, isUserOAuthed: true };
			}
			return entry;
		}),
	}));

	return newThirdPartyConfigs;
};

export const useOAuthHandlers: ({
	product,
	connectorSource,
	successFlagTitle,
	failFlagTitle,
	outboundAuthUrl,
	onCompletedAction,
	onDismissAction,
	onFailAction,
	isOnSearchDialog,
	isBulkConnect,
	query,
	datasourceId,
}: OAuthHandlersProps) => {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
	onSuccess: (oAuthSource: OAuthSource) => void;
	onFail: (oAuthSource: OAuthSource, error: Error) => void;
} = ({
	product,
	connectorSource,
	successFlagTitle,
	failFlagTitle,
	outboundAuthUrl,
	onCompletedAction = () => {},
	onDismissAction = () => {},
	onFailAction = () => {},
	isOnSearchDialog,
	isBulkConnect,
	query = '',
	datasourceId,
	extraAttributes,
}: OAuthHandlersProps) => {
	const [cloudId] = useCloudId();
	const [user] = useUser();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const setThirdPartyConfigs = useSetThirdPartyConfigs();

	// Keep a ref to the latest thirdPartyConfigs so that the onSuccess closure
	// (which fires asynchronously after the auth popup closes) always reads the
	// current store value instead of a stale captured snapshot.
	const thirdPartyConfigsRef = React.useRef(thirdPartyConfigs);
	thirdPartyConfigsRef.current = thirdPartyConfigs;
	const { generateSearchUrl, queryParams } = useAppContext();
	const userId = user?.id;
	const { retrySearch } = useSearchActions();
	const { showFlag } = useFlags();
	const { setQuickFindOpen } = useQuickFindActions();
	const oAuthAnalyticsProps = {
		product: product || '',
		connectorSource,
		cloudId: cloudId || '',
		isOnSearchDialog,
		thirdPartyConfigs,
		extraAttributes,
	};

	const fireOAuthStartedEvent = useFireOAuthStarted(oAuthAnalyticsProps);
	const fireOAuthCompletedEvent = useFireOAuthCompleted(oAuthAnalyticsProps);
	const fireOAuthFailedEvent = useFireOAuthFailed(oAuthAnalyticsProps);

	const onSuccess = (oAuthSource: OAuthSource): void => {
		fireOAuthCompletedEvent(oAuthSource);
		if (product === 'slack') {
			sendSlackAuthSuccessRequest({ cloudId });
		}
		if (product === 'teams' && fg('ai_3p_is_teams_platform_connector_enabled')) {
			sendTeamsAuthSuccessRequest({ cloudId });
		}

		onCompletedAction();

		showFlag({
			title: successFlagTitle,
			icon: <SuccessIcon spacing="spacious" label="" color={token('color.icon.success', G300)} />,
			isAutoDismiss: true,
		});

		const refreshCacheFn = serveSearchCombinedConfigExperiment()
			? refreshCombinedOAuthCache
			: refreshOAuthCache;

		const newOutboundAuthConfigs = refreshCacheFn(
			product as ProductKeys3P,
			userId || '',
			cloudId || '',
			// Read from ref to get the latest store value, not the stale closure snapshot.
			thirdPartyConfigsRef.current || {},
			datasourceId,
		);

		setThirdPartyConfigs(newOutboundAuthConfigs);
		searchPageQueryCache.clear();
		retrySearch();

		if (!isBulkConnect && isOnSearchDialog) {
			setQuickFindOpen(false);
			/* TODO: use onNavigate callback once bootstrap store is lifted. Details in https://hello.atlassian.net/wiki/spaces/CASE/pages/4670854655/3P+Discoverability+Bug
			const onNavigateCallback =
				typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;
			onNavigateCallback(
				generateSearchUrl({
					...queryParams,
					text: query,
					product: product,
				}),
				'push',
			);
			*/
			const searchUrl = generateSearchUrl({
				...queryParams,
				text: query,
				product: product,
			});
			window.location.href = searchUrl;
		}
	};

	//eslint-disable-next-line @typescript-eslint/no-unused-vars
	const onFail = (oAuthSource: OAuthSource, error: Error): void => {
		fireOAuthFailedEvent(oAuthSource);
		onFailAction();
		showFlag({
			title: failFlagTitle,
			icon: <ErrorIcon spacing="spacious" label="" color={token('color.icon.danger', R400)} />,
			isAutoDismiss: true,
		});
	};

	const onAuthorize = (oAuthSource: OAuthSource): void => {
		fireOAuthStartedEvent(oAuthSource);
		auth(outboundAuthUrl)
			.then(() => onSuccess(oAuthSource))
			.catch((error) => onFail(oAuthSource, error));
	};

	const onDismiss = (): void => {
		onDismissAction();
	};

	return { onAuthorize, onDismiss, onSuccess, onFail };
};

export const useIsUserOAuthed: (
	productKey: ProductKeys | NounKeys | undefined,
	selectedDatasourceId?: string | undefined,
) =>
	| {
			userNeedsOAuth: undefined;
			outboundAuthUrl: undefined;
	  }
	| {
			userNeedsOAuth: boolean;
			outboundAuthUrl: string;
	  } = (
	productKey: ProductKeys | NounKeys | undefined,
	selectedDatasourceId?: string | undefined,
) => {
	const [thirdPartyConfigsValue] = useThirdPartyConfigs();
	return isUserOAuthed(productKey, thirdPartyConfigsValue, selectedDatasourceId);
};

export const isUserOAuthed: (
	productKey: ProductKeys | NounKeys | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	selectedDatasourceId?: string,
) =>
	| {
			userNeedsOAuth: undefined;
			outboundAuthUrl: undefined;
	  }
	| {
			userNeedsOAuth: boolean;
			outboundAuthUrl: string;
	  } = (
	productKey: ProductKeys | NounKeys | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	selectedDatasourceId?: string,
) => {
	let userNeedsOAuth, outboundAuthUrl;

	// Handle Forge connectors first (they're not in is3pProductKey)
	if (productKey && isForgeConnectorProductKey(productKey)) {
		const productOutboundAuthConfig = thirdPartyConfigs[productKey];
		if (!productOutboundAuthConfig) {
			return { userNeedsOAuth, outboundAuthUrl };
		}
		userNeedsOAuth = productOutboundAuthConfig.userNeedsOAuth;
		outboundAuthUrl = productOutboundAuthConfig.outboundAuthUrl;
		return { userNeedsOAuth, outboundAuthUrl };
	}

	if (!productKey || !is3pProductKey(productKey)) {
		return { userNeedsOAuth, outboundAuthUrl };
	}

	// If datasource is provided, check specific datasource config
	if (selectedDatasourceId) {
		const datasourceOutboundAuthConfig = thirdPartyConfigs[productKey]?.datasources?.find(
			(datasource) => datasource.datasourceId === selectedDatasourceId,
		);
		if (!datasourceOutboundAuthConfig) {
			return { userNeedsOAuth, outboundAuthUrl };
		}
		userNeedsOAuth = datasourceOutboundAuthConfig.userNeedsOAuth;
		outboundAuthUrl = datasourceOutboundAuthConfig.outboundAuthUrl;

		return { userNeedsOAuth, outboundAuthUrl };
	}

	// If no datasource provided, check base product config
	const productOutboundAuthConfig = thirdPartyConfigs[productKey];
	if (!productOutboundAuthConfig) {
		return { userNeedsOAuth, outboundAuthUrl };
	}
	userNeedsOAuth = productOutboundAuthConfig.userNeedsOAuth;
	outboundAuthUrl = productOutboundAuthConfig.outboundAuthUrl;

	return { userNeedsOAuth, outboundAuthUrl };
};
